import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvestigationTemplates = ({ templates = [], onSelectTemplate }) => {
  const getTemplateIcon = (templateName) => {
    const name = templateName?.toLowerCase() || '';
    if (name?.includes('exchange') || name?.includes('hack')) return 'Building2';
    if (name?.includes('ransomware')) return 'Shield';
    if (name?.includes('mixer') || name?.includes('laundering')) return 'Shuffle';
    if (name?.includes('scam') || name?.includes('fraud')) return 'AlertTriangle';
    if (name?.includes('defi') || name?.includes('smart')) return 'Cpu';
    return 'FileText';
  };

  const getTemplateColor = (templateName) => {
    const name = templateName?.toLowerCase() || '';
    if (name?.includes('exchange') || name?.includes('hack')) return 'bg-primary/10 text-primary';
    if (name?.includes('ransomware')) return 'bg-error/10 text-error';
    if (name?.includes('mixer') || name?.includes('laundering')) return 'bg-warning/10 text-warning';
    if (name?.includes('scam') || name?.includes('fraud')) return 'bg-accent/10 text-accent';
    if (name?.includes('defi') || name?.includes('smart')) return 'bg-success/10 text-success';
    return 'bg-muted text-text-secondary';
  };

  const handleTemplateSelect = (template) => {
    // Parse template data and pass to callback
    const templateData = {
      id: template?.id,
      name: template?.name,
      description: template?.description,
      template_data: template?.template_data,
      steps: template?.template_data?.steps || [],
      required_data: template?.template_data?.required_data || [],
      usage_count: template?.usage_count
    };
    
    onSelectTemplate?.(templateData);
  };

  if (templates?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-forensic-card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="BookOpen" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Investigation Templates</h3>
        </div>
        
        <div className="text-center py-12">
          <Icon name="FileText" size={48} color="var(--color-text-secondary)" className="mx-auto mb-4" />
          <p className="text-text-secondary text-sm">No templates available</p>
          <p className="text-text-secondary text-xs mt-1">Templates help standardize investigation workflows</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="BookOpen" size={24} color="var(--color-primary)" />
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Investigation Templates</h2>
            <p className="text-text-secondary text-sm">Pre-configured workflows for common investigation types</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates?.map((template) => (
          <div key={template?.id} className="bg-card border border-border rounded-lg shadow-forensic-card hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getTemplateColor(template?.name)}`}>
                  <Icon 
                    name={getTemplateIcon(template?.name)} 
                    size={24} 
                    color="currentColor"
                  />
                </div>
                
                <div className="flex items-center space-x-2 text-xs text-text-secondary">
                  <Icon name="Users" size={12} color="currentColor" />
                  <span>{template?.usage_count || 0} uses</span>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {template?.name}
              </h3>
              
              <p className="text-sm text-text-secondary mb-4 line-clamp-3">
                {template?.description}
              </p>
              
              {/* Template Steps Preview */}
              {template?.template_data?.steps && template?.template_data?.steps?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-text-primary mb-2">Key Steps:</p>
                  <ul className="text-xs text-text-secondary space-y-1">
                    {template?.template_data?.steps?.slice(0, 3)?.map((step, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <div className="w-1 h-1 bg-primary rounded-full flex-shrink-0"></div>
                        <span className="truncate">{step}</span>
                      </li>
                    ))}
                    {template?.template_data?.steps?.length > 3 && (
                      <li className="text-text-secondary/60">
                        +{template?.template_data?.steps?.length - 3} more steps
                      </li>
                    )}
                  </ul>
                </div>
              )}
              
              {/* Required Data */}
              {template?.template_data?.required_data && template?.template_data?.required_data?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-text-primary mb-2">Required Data:</p>
                  <div className="flex flex-wrap gap-1">
                    {template?.template_data?.required_data?.slice(0, 3)?.map((dataType, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-muted text-text-secondary rounded">
                        {dataType?.replace('_', ' ')}
                      </span>
                    ))}
                    {template?.template_data?.required_data?.length > 3 && (
                      <span className="px-2 py-1 text-xs bg-muted text-text-secondary rounded">
                        +{template?.template_data?.required_data?.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <Button 
                onClick={() => handleTemplateSelect(template)}
                className="w-full"
                iconName="Play"
                iconPosition="left"
              >
                Use Template
              </Button>
            </div>
            
            <div className="px-6 py-3 bg-muted rounded-b-lg border-t border-border">
              <div className="flex items-center justify-between text-xs text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="User" size={12} color="currentColor" />
                  <span>{template?.creator?.full_name || 'System'}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={12} color="currentColor" />
                  <span>{new Date(template?.created_at)?.toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center py-8">
        <p className="text-text-secondary text-sm mb-4">
          Need a custom template for your investigation workflow?
        </p>
        <Button variant="outline" iconName="Plus" iconPosition="left">
          Create Custom Template
        </Button>
      </div>
    </div>
  );
};

export default InvestigationTemplates;